/**
 * Helpers for the share-link "land back on the original ticket" redirect.
 *
 * The share-link email gate stashes a `redirectTo` path in sessionStorage
 * BEFORE the user signs in via WorkOS or sets a password on the
 * add-password page (either of which can trigger a full-page reload that
 * unmounts the originating component). After the user finishes the
 * sign-in / sign-up flow, the add-password landing pulls the path back
 * out of sessionStorage and navigates there instead of the default
 * /ask-puntt landing.
 *
 * Two security invariants that EVERY caller MUST honour:
 *  1. Only navigate to internal paths (`/foo`). Reject anything that
 *     could be interpreted as an external host (`//evil.com`,
 *     `https://...`) — open-redirect protection.
 *  2. ALWAYS clear the key on read so a future, unrelated sign-in flow
 *     can't accidentally inherit a stale share-link redirect.
 *
 * The helpers are trivial to get wrong (typo `value.startsWith("/")` ->
 * drop the `!startsWith("//")` check and you have an open redirect).
 * Centralizing them here means there's one set of unit tests guarding
 * the contract for every consumer.
 */

export const POST_ONBOARDING_REDIRECT_KEY = "puntt.postOnboardingRedirect";

/**
 * Validate that an arbitrary value is a safe-to-navigate-to internal app
 * path. Returns the path on success or `undefined` on failure.
 */
export function safeInternalPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  // Must be an absolute internal path. Bare "/", protocol-relative
  // ("//evil.com/..."), and absolute URLs ("https://...") are all
  // rejected.
  if (!value.startsWith("/")) return undefined;
  if (value.startsWith("//")) return undefined;
  return value;
}

/**
 * Read-and-clear the stashed share-link redirect target from
 * sessionStorage. Returns `undefined` (the safe default) when there
 * is nothing stashed, when sessionStorage is unavailable (SSR / locked-
 * down browsers), or when the stashed value fails `safeInternalPath`.
 *
 * Always clears the key on read, including when the stored value fails
 * validation, so a malformed entry can't keep poisoning subsequent
 * reads.
 */
export function consumeShareLinkRedirect(): string | undefined {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(POST_ONBOARDING_REDIRECT_KEY);
  } catch {
    return undefined;
  }
  if (!stored) return undefined;
  try {
    sessionStorage.removeItem(POST_ONBOARDING_REDIRECT_KEY);
  } catch {
    // ignore
  }
  return safeInternalPath(stored);
}

/**
 * Stash the redirect target. No-ops silently when sessionStorage is
 * unavailable. Skips invalid values so callers can pass through an
 * untrusted query param without pre-validating.
 */
export function stashShareLinkRedirect(value: unknown): void {
  const safe = safeInternalPath(value);
  if (!safe) return;
  try {
    sessionStorage.setItem(POST_ONBOARDING_REDIRECT_KEY, safe);
  } catch {
    // ignore
  }
}
